"use client"

import { useState, useEffect } from "react"
import { Heart, MapPin, Calendar } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { Button, cn, Image, Badge } from "@city-sage/aurum-avis-labs-components"
import { Itinerary } from "@city-sage/core-client"
import { Avatar } from "./Avatar"
import { useReactions } from "../lib/useReactions"
import { MarkdownRenderer } from "./MarkdownRenderer"

interface ItineraryCardProps {
  itinerary: Itinerary
  link: string
  images?: string[]
  showStateBadge?: boolean
}

export function ItineraryCard({ itinerary, link, images = [], showStateBadge = false }: ItineraryCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'itinerary',
    reactableId: itinerary.id
  })

  // Use images prop if available, otherwise fall back to main itinerary image
  const displayImages = images && images.length > 0 
    ? images.filter(Boolean) // Remove any null/undefined URLs
    : itinerary.imageUrl 
      ? [itinerary.imageUrl] 
      : []
  
  const currentImage = displayImages[currentImageIndex] || itinerary.imageUrl || ''

  // Auto-advance slideshow every 3 seconds
  useEffect(() => {
    if (displayImages.length <= 1) return

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % displayImages.length
          )
          return 0
        }
        return prev + (100 / 30) // 30 steps over 3 seconds (100ms intervals)
      })
    }, 100)

    return () => clearInterval(interval)
  }, [displayImages.length, currentImageIndex])

  // Reset progress when image changes
  useEffect(() => {
    setProgress(0)
  }, [currentImageIndex])

  const handleLike = () => {
    toggleLike()
  }

  const getStateBadgeVariant = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'default'
      case 'PRIVATE':
        return 'secondary'
      case 'DRAFT':
        return 'outline'
      default:
        return 'secondary'
    }
  }

  const getStateBadgeText = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'Public'
      case 'PRIVATE':
        return 'Private'
      case 'DRAFT':
        return 'Draft'
      default:
        return visibility
    }
  }

  return (
    <div
      className="group relative overflow-hidden rounded-xl transition-all duration-300 hover:shadow-lg"
    >
      <Link to={link} className="block">
        <div className="relative aspect-[3/5] h-80 w-full overflow-hidden">
          {/* Progress bars at the top */}
          {displayImages.length > 1 && (
            <div className="absolute top-2 left-2 right-2 z-20 flex gap-1">
              {displayImages.map((_, index) => (
                <div
                  key={index}
                  className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
                >
                  <div
                    className={cn(
                      "h-full bg-white transition-all duration-100 ease-linear rounded-full",
                      index < currentImageIndex && "w-full",
                      index === currentImageIndex && "transition-none",
                      index > currentImageIndex && "w-0"
                    )}
                    style={{
                      width: index === currentImageIndex ? `${progress}%` : 
                             index < currentImageIndex ? '100%' : '0%'
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* State Badge */}
          {showStateBadge && (
            <div className="absolute top-2 right-2 z-20">
              <Badge variant={getStateBadgeVariant(itinerary.visibility)}>
                {getStateBadgeText(itinerary.visibility)}
              </Badge>
            </div>
          )}

          <Image
            src={currentImage}
            image={itinerary.image}
            alt={itinerary.title}
            className="w-full h-full"
            imageClassName="h-full w-full object-cover transition-all duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/70"></div>

          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h3 className="text-lg font-bold mb-1">{itinerary.title}</h3>
            <div className="line-clamp-2 text-sm text-gray-200 mb-3">
              <MarkdownRenderer content={itinerary.description || ''} className="text-gray-200 text-sm" />
            </div>

            <div className="flex items-center gap-3 text-xs text-gray-200 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {itinerary.daysCount} {itinerary.daysCount === 1 ? "day" : "days"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{itinerary.stopsCount} stops</span>
              </div>
            </div>

            {/* Author and Like section */}
            <div className="flex items-center justify-between">
              <Link 
                to="/users/$userId" 
                params={{ userId: itinerary.account.id }}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                  <Avatar className="h-6 w-6" textClassName="text-xs" account={itinerary.account} />
                <span className="text-sm font-medium text-white underline">
                  @{itinerary.account.userName || 'Anonymous'}
                </span>
              </Link>
              
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-white">{likeCount}</span>
                <Button
                  variant="secondary"
                  size="icon"
                  disabled={isPending}
                  className="h-7 w-7 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white"
                  onClick={(e) => {
                    e.preventDefault()
                    handleLike()
                  }}
                >
                  <Heart className={cn("h-3 w-3 transition-colors", isLiked ? "fill-red-500 text-red-500" : "text-gray-600")} />
                  <span className="sr-only">Like</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  )
}
